import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { LegendList, type LegendListRef } from '@legendapp/list/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import {
  Bookmark,
  CalendarDays,
  ChevronDown,
  Clock,
  Grid2x2,
  Heart,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  Share2,
  Sparkles,
  Timer,
  TreeDeciduous,
  Waves,
  Zap,
} from 'lucide-react'
import { type TouchEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppSidebar } from '@/components/app-sidebar'
import { CanopyAvatar } from '@/components/canopy-avatar'
import type { PlaybookContext } from '@/components/coach-chat-sheet'
import { MilestonePreview, planAge, planPct } from '@/components/discover-plan-preview'
import { DiscoverTemplates, type TemplateFilter } from '@/components/discover-templates'
import { LeaderboardRankRow } from '@/components/leaderboard-rank-row'
import { LeaderboardUserSheet } from '@/components/leaderboard-user-sheet'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { SiteHeader } from '@/components/site-header'
import { StreakFlame } from '@/components/streak-icons'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UsePlanDialog } from '@/components/use-plan-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { authClient } from '@/lib/auth-client'
import { useIsCoaching } from '@/lib/coaching-mode'
import { removeGoal } from '@/lib/goals-optimistic'
import { PLAYBOOKS } from '@/lib/playbooks'
import { formatCount, goalSlug } from '@/lib/utils'

const PLAYBOOK_ICONS: Record<string, React.ReactNode> = {
  parkinsonsLaw: <Clock className="h-16 w-16" />,
  pomodoro: <Timer className="h-16 w-16" />,
  twoMinuteRule: <Zap className="h-16 w-16" />,
  eisenhower: <Grid2x2 className="h-16 w-16" />,
  timeBlocking: <CalendarDays className="h-16 w-16" />,
  habitStacking: <Layers className="h-16 w-16" />,
  flowState: <Waves className="h-16 w-16" />,
  weeklyReview: <RefreshCw className="h-16 w-16" />,
  breakItDown: <TreeDeciduous className="h-16 w-16" />,
}

type DiscoverSearch = {
  tab?: 'templates' | 'following'
  mine?: boolean
  blueprints?: boolean
  filter?: string
  templatesKind?: 'goals' | 'habits'
}
const TEMPLATE_FILTERS = [
  'blueprints',
  'classics',
  'esoteric',
  'community',
  'health',
  'career',
  'mind',
  'finance',
  'relationships',
  'creative',
]

export const Route = createFileRoute('/_authenticated/explore')({
  validateSearch: (search: Record<string, unknown>): DiscoverSearch => ({
    tab: search.tab === 'templates' || search.tab === 'following' ? search.tab : undefined,
    mine: search.mine === true || search.mine === 'true' ? true : undefined,
    blueprints: search.blueprints === true || search.blueprints === 'true' ? true : undefined,
    filter:
      typeof search.filter === 'string' && TEMPLATE_FILTERS.includes(search.filter)
        ? search.filter
        : undefined,
    templatesKind:
      search.templatesKind === 'goals' || search.templatesKind === 'habits'
        ? search.templatesKind
        : undefined,
  }),
  head: () => ({
    meta: [{ title: 'Explore — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

const CATEGORY_COLORS: Record<string, string> = {
  health: '#FF6452',
  career: '#3DA9FC',
  mind: '#a8ff3e',
  finance: '#FFB020',
  relationships: '#FF5C8A',
  creative: '#2dd4bf',
}

type GoalItem = {
  _id?: string
  title: string
  category: string
  creatorId?: string
  creatorName: string
  creatorAvatar?: string | null
  creatorHandle?: string | null
  creatorStreak?: number
  milestoneCount?: number
  taskCount?: number
  doneCount?: number
  milestones?: { title: string; done: boolean }[]
  forkCount?: number
  startedAt?: number
  likeCount: number
  bookmarkCount: number
  viewerLiked: boolean
  viewerBookmarked: boolean
  coverUrl?: string | null
  posterUrl?: string | null
  coverIsVideo?: boolean
}

const BURST_CSS = `
  @keyframes gt-heart-pop {
    0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
    35%  { transform: scale(1.7) rotate(10deg);  opacity: 1; }
    60%  { transform: scale(0.88) rotate(-5deg); opacity: 1; }
    78%  { transform: scale(1.12) rotate(2deg);  opacity: 1; }
    100% { transform: scale(1.05) rotate(0deg); opacity: 0; }
  }
  @keyframes gt-ring {
    0%   { transform: scale(0.15); opacity: 1; }
    100% { transform: scale(4); opacity: 0; }
  }
  @keyframes gt-float {
    0%   { transform: translateY(0) scale(1) rotate(var(--r)); opacity: 1; }
    70%  { opacity: 0.7; }
    100% { transform: translateY(-150px) scale(0.15) rotate(calc(var(--r) + 45deg)); opacity: 0; }
  }
  @keyframes gt-heart-bounce {
    0%   { transform: scale(1); }
    30%  { transform: scale(1.45); }
    60%  { transform: scale(0.88); }
    80%  { transform: scale(1.18); }
    100% { transform: scale(1); }
  }
`

function RadialParticle({
  tx,
  ty,
  size,
  delay,
  isSpark,
}: {
  tx: number
  ty: number
  size: number
  delay: number
  isSpark: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const anim = el.animate(
      [
        { transform: 'translate(0,0) scale(1.3)', opacity: 1 },
        { transform: `translate(${tx}px,${ty}px) scale(0)`, opacity: 0 },
      ],
      { duration: 680, delay, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' },
    )
    return () => anim.cancel()
  }, [tx, ty, delay])

  return (
    <div ref={ref} className="absolute" style={{ opacity: 0 }}>
      {isSpark ? (
        <Sparkles
          style={{ width: size, height: size }}
          className="fill-pink-200/90 text-pink-200/90"
        />
      ) : (
        <Heart style={{ width: size, height: size }} className="fill-pink-400 text-pink-400" />
      )}
    </div>
  )
}

function HeartBurstOverlay({ onComplete }: { onComplete: () => void }) {
  const particles = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const angle = (i / 14) * 360 + (Math.random() - 0.5) * 20
        const rad = (angle * Math.PI) / 180
        const dist = 76 + Math.random() * 52
        return {
          id: i,
          tx: Math.cos(rad) * dist,
          ty: Math.sin(rad) * dist,
          size: 10 + Math.random() * 9,
          delay: Math.random() * 90,
          isSpark: i % 4 === 3,
        }
      }),
    [],
  )

  const floaters = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        id: i,
        left: 26 + i * 9,
        size: 8 + (i % 3) * 7,
        duration: 0.85 + i * 0.14,
        delay: i * 0.07,
        rot: -22 + i * 9,
      })),
    [],
  )

  useEffect(() => {
    const t = setTimeout(onComplete, 1500)
    return () => clearTimeout(t)
  }, [onComplete])

  return (
    <>
      <style>{BURST_CSS}</style>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
        {/* Ring ripples */}
        <div
          className="absolute rounded-full border-[3px] border-pink-400/70 w-20 h-20"
          style={{ animation: 'gt-ring 0.8s ease-out forwards' }}
        />
        <div
          className="absolute rounded-full border-2 border-pink-300/45 w-20 h-20"
          style={{ animation: 'gt-ring 0.8s ease-out 0.13s both' }}
        />
        <div
          className="absolute rounded-full border border-pink-200/25 w-20 h-20"
          style={{ animation: 'gt-ring 0.8s ease-out 0.26s both' }}
        />

        {/* Main heart with glow */}
        <div
          className="absolute drop-shadow-[0_0_36px_rgba(236,72,153,0.9)]"
          style={{ animation: 'gt-heart-pop 1.15s cubic-bezier(0.34,1.56,0.64,1) forwards' }}
        >
          <Heart className="w-28 h-28 fill-pink-400 text-pink-400" />
        </div>

        {/* Radial particle burst */}
        {particles.map((p) => (
          <RadialParticle key={p.id} {...p} />
        ))}

        {/* Floating hearts drifting upward */}
        {floaters.map(({ id, left, size, duration, delay, rot }) => (
          <div
            key={id}
            className="absolute"
            style={
              {
                left: `${left}%`,
                bottom: '40%',
                animation: `gt-float ${duration}s ease-out ${delay}s forwards`,
                '--r': `${rot}deg`,
              } as React.CSSProperties
            }
          >
            <Heart
              style={{ width: size, height: size }}
              className="fill-pink-300/90 text-pink-300/90"
            />
          </div>
        ))}
      </div>
    </>
  )
}

function fallbackBackground(color: string): string {
  return [
    'radial-gradient(120% 70% at 20% 8%, rgba(255,255,255,0.3), transparent 60%)',
    'repeating-linear-gradient(135deg, rgba(255,255,255,0.06) 0 2px, transparent 2px 16px)',
    `linear-gradient(165deg, ${color} 0%, color-mix(in srgb, ${color} 50%, #000) 100%)`,
  ].join(', ')
}

function RailButton({
  onClick,
  count,
  children,
  style,
}: {
  onClick: () => void
  count?: number
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer flex flex-col items-center gap-1 transition-transform hover:scale-110 active:scale-95"
    >
      <div
        className="flex h-11 w-11 items-center justify-center rounded-full bg-black/25 backdrop-blur-sm"
        style={style}
      >
        {children}
      </div>
      {!!count && (
        <span className="text-[11px] font-semibold text-white/80">{formatCount(count)}</span>
      )}
    </button>
  )
}

function GoalCard({
  item,
  coverRef,
  onCreatorTap,
  showFollow,
  onFollow,
  onUsePlan,
  copying,
  showSwipeHint,
  distance,
}: {
  item: GoalItem
  coverRef?: (el: HTMLElement | null) => void
  // Cards away from the active one: 0 plays, 1 buffers, further stays idle.
  distance: number
  onCreatorTap?: () => void
  showFollow: boolean
  onFollow: () => void
  onUsePlan: () => void
  copying: boolean
  showSwipeHint: boolean
}) {
  const { t } = useTranslation()
  const [liked, setLiked] = useState(item.viewerLiked)
  const [bookmarked, setBookmarked] = useState(item.viewerBookmarked)
  const [likeCount, setLikeCount] = useState(item.likeCount)
  const [bookmarkCount, setBookmarkCount] = useState(item.bookmarkCount)
  const [showHeart, setShowHeart] = useState(false)
  const [heartKey, setHeartKey] = useState(0)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (distance === 0) v.play().catch(() => {})
    else v.pause()
  }, [distance])

  // Re-sync from the reactive query when the server values change (e.g. someone
  // liked on another device). Only fires on a real server change, so it never
  // clobbers an in-flight optimistic update.
  useEffect(() => {
    setLiked(item.viewerLiked)
    setLikeCount(item.likeCount)
    setBookmarked(item.viewerBookmarked)
    setBookmarkCount(item.bookmarkCount)
  }, [item.viewerLiked, item.likeCount, item.viewerBookmarked, item.bookmarkCount])

  const toggleInteraction = useMutation(api.nodes.toggleGoalInteraction).withOptimisticUpdate(
    (store, { goalId, type }) => {
      for (const { args, value } of store.getAllQueries(api.nodes.recentGoalTitles)) {
        if (!value?.some((g) => g._id === goalId)) continue
        store.setQuery(
          api.nodes.recentGoalTitles,
          args,
          value.map((g) => {
            if (g._id !== goalId) return g
            if (type === 'like') {
              const on = !g.viewerLiked
              return {
                ...g,
                viewerLiked: on,
                likeCount: on ? g.likeCount + 1 : Math.max(0, g.likeCount - 1),
              }
            }
            const on = !g.viewerBookmarked
            return {
              ...g,
              viewerBookmarked: on,
              bookmarkCount: on ? g.bookmarkCount + 1 : Math.max(0, g.bookmarkCount - 1),
            }
          }),
        )
      }
    },
  )

  const catKey = item.category.toLowerCase()
  const bgColor = CATEGORY_COLORS[catKey] ?? '#a8ff3e'
  const categoryLabel = t(`discover.categories.${catKey}`, { defaultValue: item.category })

  async function handleLike() {
    const next = !liked
    const prevCount = likeCount
    setLiked(next)
    setLikeCount((n) => (next ? n + 1 : Math.max(0, n - 1)))
    if (next) {
      setHeartKey((k) => k + 1)
      setShowHeart(true)
    }
    if (item._id) {
      toggleInteraction({ goalId: item._id as Id<'nodes'>, type: 'like' }).catch(() => {
        setLiked(!next)
        setLikeCount(prevCount)
      })
    }
  }

  // Double-tap only ever likes (never unlikes) and always replays the heart.
  const lastTap = useRef(0)
  const downAt = useRef<{ x: number; y: number } | null>(null)
  function handleCardTap(e: React.PointerEvent) {
    if ((e.target as HTMLElement).closest('button, a, input, video[controls]')) return
    // Ignore the pointerup that ends a swipe between cards.
    const d = downAt.current
    if (!d || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 10) return
    const now = Date.now()
    if (now - lastTap.current < 300) {
      lastTap.current = 0
      if (liked) {
        setHeartKey((k) => k + 1)
        setShowHeart(true)
      } else void handleLike()
    } else lastTap.current = now
  }

  async function handleBookmark() {
    const next = !bookmarked
    const prevCount = bookmarkCount
    setBookmarked(next)
    setBookmarkCount((n) => (next ? n + 1 : Math.max(0, n - 1)))
    if (item._id) {
      toggleInteraction({ goalId: item._id as Id<'nodes'>, type: 'bookmark' }).catch(() => {
        setBookmarked(!next)
        setBookmarkCount(prevCount)
      })
    }
  }

  async function handleShare() {
    const url = item._id
      ? withShareUtm(`https://goaltree.ai/share/${goalSlug(item.title, item._id)}`, 'goal_link')
      : withShareUtm('https://goaltree.ai', 'goal_link')
    const text = t('discover.shareMessage', { title: item.title })
    const shareData = { title: item.title, text, url }
    if (navigator.canShare?.(shareData)) {
      await navigator.share(shareData).catch(() => {})
    } else {
      await navigator.clipboard.writeText(url)
      toast.success(t('discover.copied'))
    }
  }

  const hasCover = !!item.coverUrl
  const milestones = item.milestones ?? []
  const taskCount = item.taskCount ?? 0
  const doneCount = item.doneCount ?? 0
  const pct = planPct(doneCount, taskCount)
  const age = planAge(t, item.startedAt)
  const forkCount = item.forkCount ?? 0

  return (
    <div
      className="relative flex h-full w-full touch-manipulation select-none flex-col overflow-hidden snap-start text-white"
      style={hasCover ? { backgroundColor: bgColor } : { background: fallbackBackground(bgColor) }}
      onPointerDown={(e) => (downAt.current = { x: e.clientX, y: e.clientY })}
      onPointerUp={handleCardTap}
    >
      {hasCover &&
        (item.coverIsVideo ? (
          <video
            ref={(el) => {
              videoRef.current = el
              coverRef?.(el)
            }}
            src={item.coverUrl!}
            poster={item.posterUrl ?? undefined}
            preload={distance <= 1 ? 'auto' : 'none'}
            autoPlay={distance === 0}
            muted
            loop
            playsInline
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            style={{ transform: 'translateY(0) scale(1.1)', willChange: 'transform' }}
          />
        ) : (
          <img
            ref={coverRef}
            src={item.coverUrl!}
            alt=""
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            style={{ transform: 'translateY(0) scale(1.1)', willChange: 'transform' }}
          />
        ))}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-black/45 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

      {showHeart && <HeartBurstOverlay key={heartKey} onComplete={() => setShowHeart(false)} />}

      {/* Cards run under the floating tab bar; the padding keeps their content above it. */}
      <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col gap-3 px-4 pb-5 max-lg:pb-[calc(64px+max(env(safe-area-inset-bottom),10px)+0.75rem)]">
        <div className="flex items-end gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <div className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: bgColor }}
              />
              <span className="text-[10px] font-bold tracking-widest text-white/80">
                {categoryLabel.toUpperCase()}
              </span>
            </div>
            <h2 className="line-clamp-3 text-[32px] font-black leading-[1.05] tracking-tight">
              {item.title}
            </h2>
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <button
                type="button"
                className="flex min-w-0 cursor-pointer items-center gap-2 text-left"
                onClick={(e) => {
                  e.stopPropagation()
                  onCreatorTap?.()
                }}
              >
                <CanopyAvatar userId={item.creatorId}>
                  <UserAvatar
                    image={item.creatorAvatar}
                    name={item.creatorName}
                    size={28}
                    className="h-7 w-7 shrink-0 rounded-full object-cover"
                  />
                </CanopyAvatar>
                <span className="truncate font-semibold">{item.creatorName}</span>
              </button>
              {(item.creatorStreak ?? 0) > 0 && (
                <span className="shrink-0 text-white/70">
                  <StreakFlame /> {item.creatorStreak}
                </span>
              )}
              {showFollow && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onFollow()
                  }}
                  className="shrink-0 cursor-pointer rounded-full border border-white/60 px-2.5 py-0.5 text-xs font-semibold transition-colors hover:bg-white hover:text-black"
                >
                  {t('leaderboard.follow')}
                </button>
              )}
            </div>
            {(milestones.length > 0 || taskCount > 0) && (
              <div className="flex flex-col gap-3 rounded-xl bg-black/30 p-3 backdrop-blur-md lg:hidden">
                {milestones.length > 0 && (
                  <MilestonePreview milestones={milestones} tone="overlay" />
                )}
                {taskCount > 0 && (
                  <div className="flex items-center gap-2 text-[11px] font-semibold text-white/80">
                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/20">
                      <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
                    </div>
                    <span>
                      {doneCount}/{taskCount}
                    </span>
                    <span>{pct}%</span>
                    {age && <span className="text-white/60">{age}</span>}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-col items-center gap-4">
            <RailButton
              onClick={handleLike}
              count={likeCount}
              style={
                liked
                  ? { animation: 'gt-heart-bounce 0.45s cubic-bezier(0.34,1.56,0.64,1)' }
                  : undefined
              }
            >
              <Heart className="h-5 w-5" fill={liked ? 'white' : 'transparent'} stroke="white" />
            </RailButton>
            <RailButton onClick={handleBookmark} count={bookmarkCount}>
              <Bookmark
                className="h-5 w-5"
                fill={bookmarked ? 'white' : 'transparent'}
                stroke="white"
              />
            </RailButton>
            <RailButton onClick={handleShare}>
              <Share2 className="h-5 w-5 text-white" />
            </RailButton>
          </div>
        </div>

        <div className="flex flex-col items-center gap-1.5 lg:hidden">
          <button
            type="button"
            onClick={onUsePlan}
            disabled={copying}
            className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-white py-3.5 text-sm font-bold text-black transition-transform active:scale-[0.99] disabled:opacity-60"
          >
            {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t(item._id ? 'discover.usePlan' : 'discover.startGoal')}
          </button>
          {forkCount > 0 && (
            <span className="text-[11px] text-white/60">
              {t('discover.forkCount', { count: forkCount })}
            </span>
          )}
        </div>
      </div>

      {showSwipeHint && (
        <div className="pointer-events-none absolute inset-x-0 top-1/3 z-10 flex justify-center lg:hidden">
          <span className="flex animate-bounce items-center gap-1.5 rounded-full bg-black/40 px-3 py-1.5 text-xs font-semibold backdrop-blur-sm">
            {t('discover.swipeHint')}
            <ChevronDown className="h-3.5 w-3.5" />
          </span>
        </div>
      )}
    </div>
  )
}

const PLAYBOOK_DATA = PLAYBOOKS.map((p) => ({ ...p, icon: PLAYBOOK_ICONS[p.key] }))

type FeedItem =
  | { kind: 'goal'; data: GoalItem; key: string }
  | { kind: 'playbook'; idx: number; key: string }

type PlaybookItem = {
  key: string
  icon: React.ReactNode
  title: string
  hook: string
  goalPrompt: string
}

function PlaybookCard({
  item,
  onApply,
}: {
  item: PlaybookItem
  onApply: (ctx: PlaybookContext) => void
}) {
  const { t } = useTranslation()
  const does = t(`discover.playbook.${item.key}.does`, { defaultValue: '' })
  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden snap-start bg-[#0a0a0a]">
      {/* Soft glow in the live accent */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 50% 40%, color-mix(in srgb, var(--primary) 14%, transparent) 0%, transparent 65%)',
        }}
      />

      {/* Content — top padding clears the feed's tabs + category chips */}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto px-8 pt-28 pb-2 text-center">
        <span className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/15 px-3 py-1">
          <Sparkles className="h-3 w-3 text-primary-text" />
          <span className="text-[10px] font-bold tracking-widest text-primary-text">
            {t('discover.playbookBadge')}
          </span>
        </span>
        {/* The icon is decorative — drop it on short cards so the copy fits. */}
        <div className="text-primary-text/80 [@media(max-height:700px)]:hidden">{item.icon}</div>
        <div className="space-y-3">
          <h2
            className="font-black tracking-tight text-white text-balance"
            style={{
              fontSize:
                item.title.length > 12 ? 'clamp(30px, 6vh, 40px)' : 'clamp(36px, 7vh, 52px)',
              lineHeight: 1.05,
            }}
          >
            {item.title}
          </h2>
          <p className="text-base leading-relaxed text-white/60 text-balance">{item.hook}</p>
        </div>
        {does && (
          <div className="w-full max-w-sm rounded-xl bg-white/[0.06] px-4 py-3 text-left backdrop-blur">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">
              {t('discover.playbook.doesLabel')}
            </p>
            <p className="mt-1 text-sm leading-snug text-white/85">{does}</p>
          </div>
        )}
      </div>

      {/* Apply — same shape and bottom-nav clearance as the goal cards' "Use this plan" */}
      <div className="relative z-10 px-5 pb-8 max-lg:pb-[calc(64px+max(env(safe-area-inset-bottom),10px)+0.75rem)]">
        <button
          type="button"
          onClick={() =>
            onApply({ title: item.title, hook: item.hook, goalPrompt: item.goalPrompt })
          }
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 text-[15px] font-extrabold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Sparkles className="h-4 w-4" />
          {t('discover.playbookApplyCta')}
        </button>
      </div>
    </div>
  )
}

const SWIPE_HINT_KEY = 'gt-discover-swipe-hint'
const FEED_TABS = ['forYou', 'following', 'templates'] as const
const TAB_SETTLE_MS = 220
const MAX_CARD_WIDTH = 430
const DESKTOP_CARD_WIDTH = 330
const DESKTOP_CARD_MAX_HEIGHT = 700
const CATEGORY_KEYS = Object.keys(CATEGORY_COLORS)

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return isDesktop
}

function DesktopPlanPanel({
  item,
  playbook,
  canFollow,
  isFollowing,
  copying,
  onFollow,
  onUsePlan,
  onCreatorTap,
  onApply,
}: {
  item: FeedItem | undefined
  playbook: PlaybookItem[]
  canFollow: boolean
  isFollowing: boolean
  copying: boolean
  onFollow: () => void
  onUsePlan: () => void
  onCreatorTap?: () => void
  onApply: (ctx: PlaybookContext) => void
}) {
  const { t } = useTranslation()
  if (!item) return <aside className="w-[380px] shrink-0" />

  if (item.kind === 'playbook') {
    const p = playbook[item.idx]
    return (
      <aside className="flex w-[380px] shrink-0 flex-col gap-4">
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {t('discover.playbookBadge')}
        </span>
        <h2 className="text-3xl font-black tracking-tight">{p.title}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{p.hook}</p>
        <button
          type="button"
          onClick={() => onApply({ title: p.title, hook: p.hook, goalPrompt: p.goalPrompt })}
          className="w-fit cursor-pointer rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {t('discover.playbookApplyCta')}
        </button>
      </aside>
    )
  }

  const g = item.data
  const catKey = g.category.toLowerCase()
  const milestones = g.milestones ?? []
  const milestoneCount = g.milestoneCount ?? 0
  const taskCount = g.taskCount ?? 0
  const doneCount = g.doneCount ?? 0
  const forkCount = g.forkCount ?? 0
  const age = planAge(t, g.startedAt)
  const meta = [
    milestoneCount > 0 && t('discover.milestoneCount', { count: milestoneCount }),
    taskCount > 0 && t('discover.taskCount', { count: taskCount }),
    taskCount > 0 &&
      t('discover.doneCount', { count: doneCount, pct: planPct(doneCount, taskCount) }),
    age,
    forkCount > 0 && t('discover.forkCount', { count: forkCount }),
  ].filter(Boolean)

  return (
    <aside className="flex w-[380px] shrink-0 flex-col gap-4">
      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
        <span style={{ color: CATEGORY_COLORS[catKey] }}>
          {t(`discover.categories.${catKey}`, { defaultValue: g.category })}
        </span>
        {' · '}
        <button
          type="button"
          onClick={onCreatorTap}
          className="cursor-pointer uppercase hover:underline"
        >
          {t('discover.byName', { name: g.creatorName })}
        </button>
      </span>
      <h2 className="text-3xl font-black leading-tight tracking-tight">{g.title}</h2>
      {meta.length > 0 && <p className="text-sm text-muted-foreground">{meta.join(' · ')}</p>}
      {milestones.length > 0 && (
        <div className="flex flex-col gap-2 rounded-xl border border-border/60 bg-muted/20 p-4">
          <MilestonePreview milestones={milestones} tone="surface" />
          {milestoneCount > milestones.length && (
            <span className="pl-7.5 text-xs text-muted-foreground">
              {t('discover.moreMilestones', { count: milestoneCount - milestones.length })}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onUsePlan}
          disabled={copying}
          className="flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
        >
          {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {t(g._id ? 'discover.usePlan' : 'discover.startGoal')}
        </button>
        {canFollow && (
          <button
            type="button"
            onClick={onFollow}
            className="cursor-pointer rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-muted"
          >
            {isFollowing
              ? t('leaderboard.following')
              : t('discover.followName', { name: g.creatorName })}
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground/70">{t('discover.keyboardHint')}</p>
    </aside>
  )
}

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const search = Route.useSearch()
  const tab = search.tab ?? 'forYou'
  const [templateFilter, setTemplateFilter] = useState<TemplateFilter>(
    search.mine ? 'mine' : search.blueprints ? 'blueprints' : (search.filter ?? null),
  )
  useEffect(() => {
    if (search.mine) setTemplateFilter('mine')
    else if (search.blueprints) setTemplateFilter('blueprints')
    else if (search.filter) setTemplateFilter(search.filter)
  }, [search.mine, search.blueprints, search.filter])
  const [category, setCategory] = useState<string | null>(null)
  const data = useQuery(
    api.nodes.recentGoalTitles,
    tab === 'templates' ? 'skip' : { following: tab === 'following' },
  )
  const { data: session } = authClient.useSession()
  const viewerId = session?.user?.id ?? null

  const goals = useMemo<GoalItem[]>(() => {
    if (data === undefined) return []
    let list: GoalItem[] = data
    if (tab === 'forYou' && data.length < 4) {
      list = (
        t('discover.seed', { returnObjects: true }) as Array<{
          title: string
          category: string
          creatorName: string
        }>
      ).map((s) => ({
        ...s,
        likeCount: 0,
        bookmarkCount: 0,
        viewerLiked: false,
        viewerBookmarked: false,
      }))
    }
    return category ? list.filter((g) => g.category.toLowerCase() === category) : list
  }, [data, tab, category, t])

  const playbook = useMemo<PlaybookItem[]>(
    () =>
      PLAYBOOK_DATA.map((p) => ({
        ...p,
        title: t(`discover.playbook.${p.key}.title`),
        hook: t(`discover.playbook.${p.key}.hook`),
      })),
    [t],
  )

  const [selectedCreator, setSelectedCreator] = useState<{
    userId: string
    name: string
    image: string | null
  } | null>(null)

  const coaching = useIsCoaching()
  function handleApply(ctx: PlaybookContext) {
    // Coaching mode has no AI coach: the playbook seeds a plan instead.
    if (coaching) void navigate({ to: '/goals/new', search: { goal: ctx.goalPrompt } })
    else window.dispatchEvent(new CustomEvent('open-coach', { detail: { playbookContext: ctx } }))
  }

  // Mix playbook cards into the goal feed — one every 4 goals, offset by 3
  const feed = useMemo<FeedItem[]>(() => {
    const result: FeedItem[] = []
    let playbookIdx = 0
    goals.forEach((goal, i) => {
      result.push({ kind: 'goal', data: goal, key: goal._id ?? `g${i}` })
      if ((i + 1) % 4 === 3 && playbookIdx < playbook.length) {
        result.push({ kind: 'playbook', idx: playbookIdx, key: `p${playbookIdx}` })
        playbookIdx++
      }
    })
    return result
  }, [goals, playbook])

  const followingIds = useQuery(api.follows.getMyFollowingIds)
  const followMutation = useMutation(api.follows.follow).withOptimisticUpdate(
    (store, { handle }) => {
      const userId = store
        .getAllQueries(api.nodes.recentGoalTitles)
        .flatMap((q) => q.value ?? [])
        .find((g) => g.creatorHandle === handle)?.creatorId
      const cur = store.getQuery(api.follows.getMyFollowingIds, {})
      if (!userId || !cur) return
      store.setQuery(
        api.follows.getMyFollowingIds,
        {},
        cur.includes(userId) ? cur.filter((id) => id !== userId) : [...cur, userId],
      )
    },
  )
  const [followOverrides, setFollowOverrides] = useState<Record<string, boolean>>({})
  const isFollowing = (userId?: string) =>
    !!userId && (followOverrides[userId] ?? !!followingIds?.includes(userId))
  const canFollow = (g: GoalItem) =>
    !!g.creatorHandle && !!g.creatorId && g.creatorId !== viewerId && followingIds !== undefined

  function toggleFollow(g: GoalItem) {
    if (!g.creatorHandle || !g.creatorId) return
    const userId = g.creatorId
    const next = !isFollowing(userId)
    setFollowOverrides((o) => ({ ...o, [userId]: next }))
    followMutation({ handle: g.creatorHandle }).catch(() =>
      setFollowOverrides((o) => ({ ...o, [userId]: !next })),
    )
  }

  const copyPublicGoal = useMutation(api.nodes.copyPublicGoal)
  const [copyingId, setCopyingId] = useState<string | null>(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  // "Use this plan" (button or U) opens a confirm step; nothing is created until
  // the user picks "Add to my goals".
  const [confirmPlan, setConfirmPlan] = useState<GoalItem | null>(null)
  const removeNode = useMutation(api.nodes.remove).withOptimisticUpdate((store, { nodeId }) =>
    removeGoal(store, nodeId),
  )

  function handleUsePlan(g: GoalItem) {
    if (copyingId) return
    // Placeholder cards (shown while the feed is thin) have no goal to copy.
    if (!g._id) {
      navigate({ to: '/goals/new', search: { goal: g.title } })
      return
    }
    setConfirmPlan(g)
  }

  async function confirmUsePlan() {
    const g = confirmPlan
    if (!g?._id || copyingId) return
    setCopyingId(g._id)
    try {
      const newId = await copyPublicGoal({ goalId: g._id as Id<'nodes'> })
      setConfirmPlan(null)
      toast.success(t('discover.planCopied'), {
        action: {
          label: t('common.undo'),
          onClick: () => {
            void removeNode({ nodeId: newId })
              .then(() => {
                toast(t('discover.confirm.removed'))
                navigate({ to: '/explore' })
              })
              .catch(() => toast.error(t('common.error')))
          },
        },
      })
      navigate({ to: '/goals/$goalId', params: { goalId: goalSlug(g.title, newId) } })
    } catch (err) {
      if (err instanceof ConvexError && String(err.data).includes('Free plan is limited')) {
        setConfirmPlan(null)
        setUpgradeOpen(true)
      } else toast.error(t('discover.copyFailed'))
    } finally {
      setCopyingId(null)
    }
  }

  function openCreator(g: GoalItem) {
    if (!g.creatorId) return
    setSelectedCreator({ userId: g.creatorId, name: g.creatorName, image: g.creatorAvatar ?? null })
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<LegendListRef>(null)
  const [itemHeight, setItemHeight] = useState(0)
  const [itemWidth, setItemWidth] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  // The sidebar can switch tabs too; the list remounts at the top either way.
  useEffect(() => setActiveIndex(0), [tab])
  const coverRefs = useRef(new Map<number, HTMLElement>())

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      setItemHeight(rect.height)
      setItemWidth(rect.width)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [tab === 'templates'])

  const cardWidth = isDesktop ? DESKTOP_CARD_WIDTH : Math.min(itemWidth, MAX_CARD_WIDTH)
  const cardHeight = isDesktop ? Math.min(itemHeight - 48, DESKTOP_CARD_MAX_HEIGHT) : itemHeight
  const rounded = isDesktop || itemWidth > MAX_CARD_WIDTH
  const cardHeightRef = useRef(0)
  cardHeightRef.current = cardHeight

  function handleDiscoverScroll(e: { nativeEvent: { contentOffset: { y: number } } }) {
    const scrollY = e.nativeEvent.contentOffset.y
    const h = cardHeightRef.current
    if (!h) return
    setActiveIndex(Math.round(scrollY / h))
    coverRefs.current.forEach((img, idx) => {
      const relPos = (scrollY - idx * h) / h
      const clamped = Math.max(-1, Math.min(1, relPos))
      img.style.transform = `translateY(${clamped * -30}px) scale(1.1)`
    })
  }

  const [hintSeen, setHintSeen] = useState(() => {
    try {
      return localStorage.getItem(SWIPE_HINT_KEY) === '1'
    } catch {
      return true
    }
  })
  useEffect(() => {
    if (hintSeen || activeIndex === 0) return
    setHintSeen(true)
    try {
      localStorage.setItem(SWIPE_HINT_KEY, '1')
    } catch {}
  }, [activeIndex, hintSeen])

  function selectFeed(nextTab: typeof tab, nextCategory: string | null) {
    if (nextTab !== tab)
      void navigate({
        to: '/explore',
        search: nextTab === 'forYou' ? {} : { tab: nextTab },
        replace: true,
      })
    setCategory(nextCategory)
    setActiveIndex(0)
  }

  // Swipe sideways between the top tabs (as in Vaboo): the content follows the finger, the
  // underline slides toward the neighbour, and letting go past a quarter (or flicking) switches.
  // Swipes starting on sideways scrollers ([data-noswipe]) or in a dialog are left alone.
  // `enter`: the incoming tab's slide-in (not a finger), so no drag line is drawn for it.
  const [drag, setDrag] = useState<{ dx: number; animate: boolean; enter?: boolean }>({
    dx: 0,
    animate: false,
  })
  // The tab a released swipe is gliding to, underlined straight away (not after the glide).
  const [committing, setCommitting] = useState<(typeof FEED_TABS)[number] | null>(null)
  const shownTab = committing ?? tab
  const swipeStart = useRef<{ x: number; y: number; t: number; axis: 'x' | 'y' | null } | null>(
    null,
  )
  const tabIndex = FEED_TABS.indexOf(tab)
  const neighbour = (dx: number) => FEED_TABS[tabIndex + (dx < 0 ? 1 : -1)] ?? null
  const swipeHandlers = {
    onTouchStart: (e: TouchEvent) => {
      const touch = e.touches[0]
      const skip = (e.target as HTMLElement).closest('[data-noswipe],[role="dialog"]')
      swipeStart.current =
        touch && !skip ? { x: touch.clientX, y: touch.clientY, t: e.timeStamp, axis: null } : null
    },
    onTouchMove: (e: TouchEvent) => {
      const s = swipeStart.current
      const touch = e.touches[0]
      if (!s || !touch) return
      const dx = touch.clientX - s.x
      const dy = touch.clientY - s.y
      if (!s.axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
        s.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y'
      }
      if (s.axis !== 'x') return
      // No tab that way: a little give, then it springs back.
      setDrag({ dx: neighbour(dx) ? dx : dx / 4, animate: false })
    },
    onTouchEnd: (e: TouchEvent) => {
      const s = swipeStart.current
      const touch = e.changedTouches[0]
      swipeStart.current = null
      if (!s || !touch || s.axis !== 'x') return
      const dx = touch.clientX - s.x
      const w = window.innerWidth
      const speed = dx / Math.max(1, e.timeStamp - s.t)
      const next = neighbour(dx)
      if (next && (Math.abs(dx) > w / 4 || Math.abs(speed) > 0.5)) {
        setCommitting(next)
        setDrag({ dx: dx < 0 ? -w : w, animate: true })
        window.setTimeout(() => {
          selectFeed(next, category)
          setCommitting(null)
          // The new tab comes in from the side it was swiped toward.
          setDrag({ dx: dx < 0 ? w * 0.3 : -w * 0.3, animate: false, enter: true })
          requestAnimationFrame(() =>
            requestAnimationFrame(() => setDrag({ dx: 0, animate: true })),
          )
        }, TAB_SETTLE_MS)
      } else setDrag({ dx: 0, animate: true })
    },
    onTouchCancel: () => {
      if (swipeStart.current?.axis === 'x') setDrag({ dx: 0, animate: true })
      swipeStart.current = null
    },
  }
  const slideStyle = {
    transform: drag.dx ? `translateX(${drag.dx}px)` : undefined,
    opacity: drag.dx ? 1 - Math.min(0.6, Math.abs(drag.dx) / window.innerWidth) : undefined,
    transition: drag.animate
      ? `transform ${TAB_SETTLE_MS}ms ease-out, opacity ${TAB_SETTLE_MS}ms ease-out`
      : 'none',
  }
  const tabRefs = useRef(new Map<string, HTMLElement>())
  // While dragging, a line slides from the active tab toward the one coming in.
  const dragLine = (() => {
    if (!drag.dx || drag.animate || drag.enter || typeof window === 'undefined') return null
    const at = (key: string | null) => {
      const el = key ? tabRefs.current.get(key) : undefined
      return el?.isConnected ? { left: el.offsetLeft, width: el.offsetWidth } : null
    }
    const a = at(tab)
    const b = at(neighbour(drag.dx))
    if (!a || !b) return null
    const p = Math.min(1, Math.abs(drag.dx) / window.innerWidth)
    return { left: a.left + (b.left - a.left) * p, width: a.width + (b.width - a.width) * p }
  })()

  const kbRef = useRef({ feed, activeIndex, handleUsePlan })
  kbRef.current = { feed, activeIndex, handleUsePlan }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      )
        return
      if (document.querySelector('[role="dialog"]')) return
      const { feed, activeIndex, handleUsePlan } = kbRef.current
      const key = e.key?.toLowerCase()
      const step =
        key === 'arrowdown' || key === 'j' ? 1 : key === 'arrowup' || key === 'k' ? -1 : 0
      if (step) {
        e.preventDefault()
        const next = Math.max(0, Math.min(feed.length - 1, activeIndex + step))
        listRef.current?.scrollToIndex({ index: next, animated: true })
        setActiveIndex(next)
      } else if (key === 'u') {
        const item = feed[activeIndex]
        if (item?.kind === 'goal') {
          e.preventDefault()
          void handleUsePlan(item.data)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const activeItem = feed[activeIndex]
  const activeGoal = activeItem?.kind === 'goal' ? activeItem.data : null
  // Tabs and chips sit on a cover image only when the feed has cards; elsewhere (templates,
  // loading, an empty Following) they're on the page background and use theme colours.
  const onMedia = tab !== 'templates' && data !== undefined && feed.length > 0
  const pill = (selected: boolean) =>
    `shrink-0 cursor-pointer rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
      onMedia
        ? `backdrop-blur-sm ${selected ? 'bg-white text-black' : 'bg-black/35 text-white/90 hover:bg-black/50'}`
        : selected
          ? 'bg-foreground text-background'
          : 'bg-muted text-foreground/80 hover:bg-muted/70'
    }`

  const tabButtons = (
    <div className="pointer-events-auto relative mx-auto flex items-center justify-center gap-4 text-sm font-bold">
      {dragLine && (
        <span
          aria-hidden
          className={`pointer-events-none absolute bottom-0 h-0.5 rounded-full ${onMedia ? 'bg-white' : 'bg-foreground'}`}
          style={{ left: dragLine.left, width: dragLine.width }}
        />
      )}
      {FEED_TABS.map((key) => (
        <button
          key={key}
          ref={(el) => {
            if (el) tabRefs.current.set(key, el)
          }}
          type="button"
          onClick={() => selectFeed(key, category)}
          className={`cursor-pointer border-b-2 pb-0.5 transition-colors ${
            onMedia
              ? `drop-shadow ${shownTab === key ? 'text-white' : 'text-white/60'}`
              : shownTab === key
                ? 'text-foreground'
                : 'text-muted-foreground'
          } ${
            shownTab === key && !dragLine
              ? onMedia
                ? 'border-white'
                : 'border-foreground'
              : 'border-transparent'
          }`}
        >
          {t(`discover.tabs.${key}`)}
        </button>
      ))}
    </div>
  )

  return (
    <SidebarProvider className="h-dvh overflow-hidden">
      <AppSidebar />
      <SidebarInset className="relative flex flex-col overflow-hidden">
        <SiteHeader transparent className="hidden lg:flex" />
        {tab === 'templates' ? (
          <div
            className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-background text-foreground"
            {...swipeHandlers}
          >
            <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 pt-[max(env(safe-area-inset-top),14px)] pb-8 max-lg:pb-[calc(64px+max(env(safe-area-inset-bottom),10px)+0.75rem)]">
              {tabButtons}
              <div style={slideStyle}>
                <DiscoverTemplates
                  filter={templateFilter}
                  onFilterChange={setTemplateFilter}
                  kind={search.templatesKind}
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="flex min-h-0 flex-1 items-center justify-center gap-12"
          >
            {itemHeight > 0 && itemWidth > 0 && (
              <div
                className="relative shrink-0 overflow-hidden"
                style={{ width: cardWidth, height: cardHeight, borderRadius: rounded ? '1rem' : 0 }}
                {...swipeHandlers}
              >
                <div className="absolute inset-0" style={slideStyle}>
                  {data === undefined ? (
                    <div className="absolute inset-0 animate-pulse bg-muted/50">
                      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 px-4 pb-5 max-lg:pb-[calc(64px+max(env(safe-area-inset-bottom),10px)+0.75rem)]">
                        <div className="flex items-end gap-3">
                          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                            <div className="h-2.5 w-20 rounded bg-muted-foreground/15" />
                            <div className="h-8 w-full rounded-lg bg-muted-foreground/15" />
                            <div className="h-8 w-3/5 rounded-lg bg-muted-foreground/15" />
                            <div className="flex items-center gap-2">
                              <div className="h-7 w-7 rounded-full bg-muted-foreground/15" />
                              <div className="h-3.5 w-28 rounded bg-muted-foreground/15" />
                            </div>
                            <div className="h-20 rounded-xl bg-muted-foreground/10 lg:hidden" />
                          </div>
                          <div className="flex shrink-0 flex-col items-center gap-4">
                            {[0, 1, 2].map((i) => (
                              <div
                                key={i}
                                className="h-11 w-11 rounded-full bg-muted-foreground/15"
                              />
                            ))}
                          </div>
                        </div>
                        <div className="h-12 rounded-full bg-muted-foreground/15 lg:hidden" />
                      </div>
                    </div>
                  ) : feed.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center gap-2 bg-muted/30 px-8 text-center">
                      <p className="font-semibold">
                        {tab === 'following' && !category
                          ? t('discover.emptyFollowing')
                          : t('discover.emptyCategory')}
                      </p>
                      {tab === 'following' && !category && (
                        <p className="text-sm text-muted-foreground">
                          {t('discover.emptyFollowingDesc')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <LegendList
                      key={`${tab}:${category ?? ''}`}
                      ref={listRef}
                      recycleItems={false}
                      data={feed}
                      keyExtractor={(item) => item.key}
                      extraData={activeIndex}
                      renderItem={({ item, index }) => (
                        // Pure CSS paging (as in Vaboo): mandatory snap on the list plus
                        // snap-stop on every card, so a fast flick or wheel spin can't
                        // carry past the next card.
                        <div
                          style={{
                            height: cardHeight,
                            scrollSnapAlign: 'start',
                            scrollSnapStop: 'always',
                          }}
                        >
                          {item.kind === 'goal' ? (
                            <GoalCard
                              item={item.data}
                              coverRef={(el) => {
                                if (el) coverRefs.current.set(index, el)
                                else coverRefs.current.delete(index)
                              }}
                              onCreatorTap={
                                item.data.creatorId ? () => openCreator(item.data) : undefined
                              }
                              showFollow={canFollow(item.data) && !isFollowing(item.data.creatorId)}
                              onFollow={() => toggleFollow(item.data)}
                              onUsePlan={() => handleUsePlan(item.data)}
                              copying={copyingId !== null && copyingId === item.data._id}
                              showSwipeHint={index === 0 && !hintSeen && feed.length > 1}
                              distance={Math.abs(index - activeIndex)}
                            />
                          ) : (
                            <PlaybookCard item={playbook[item.idx]} onApply={handleApply} />
                          )}
                        </div>
                      )}
                      estimatedItemSize={cardHeight}
                      estimatedListSize={{ height: cardHeight, width: cardWidth }}
                      onScroll={handleDiscoverScroll}
                      showsVerticalScrollIndicator={false}
                      style={{
                        height: cardHeight,
                        width: '100%',
                        scrollbarWidth: 'none',
                        scrollSnapType: 'y mandatory',
                        overscrollBehavior: 'contain',
                      }}
                    />
                  )}
                </div>

                <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex flex-col gap-2.5 pt-[max(env(safe-area-inset-top),14px)]">
                  {tabButtons}
                  <div
                    data-noswipe
                    className="pointer-events-auto flex gap-1.5 overflow-x-auto px-4"
                    style={{ scrollbarWidth: 'none' }}
                  >
                    <button
                      type="button"
                      onClick={() => selectFeed(tab, null)}
                      className={pill(category === null)}
                    >
                      {t('discover.allCategories')}
                    </button>
                    {CATEGORY_KEYS.map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => selectFeed(tab, key)}
                        className={pill(category === key)}
                      >
                        {t(`discover.categories.${key}`)}
                      </button>
                    ))}
                  </div>
                  <LeaderboardRankRow
                    onMedia={onMedia}
                    className="pointer-events-auto mx-4 lg:hidden"
                  />
                </div>
              </div>
            )}
            {isDesktop && itemHeight > 0 && (
              <DesktopPlanPanel
                item={activeItem}
                playbook={playbook}
                canFollow={!!activeGoal && canFollow(activeGoal)}
                isFollowing={isFollowing(activeGoal?.creatorId)}
                copying={copyingId !== null && copyingId === activeGoal?._id}
                onFollow={() => activeGoal && toggleFollow(activeGoal)}
                onUsePlan={() => activeGoal && handleUsePlan(activeGoal)}
                onCreatorTap={activeGoal?.creatorId ? () => openCreator(activeGoal) : undefined}
                onApply={handleApply}
              />
            )}
          </div>
        )}
        <LeaderboardUserSheet
          userId={selectedCreator?.userId ?? null}
          name={selectedCreator?.name ?? ''}
          image={selectedCreator?.image ?? null}
          onClose={() => setSelectedCreator(null)}
        />
        <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="goal-limit" />
        <UsePlanDialog
          plan={confirmPlan}
          copying={!!copyingId}
          onClose={() => setConfirmPlan(null)}
          onConfirm={() => void confirmUsePlan()}
          onPlanOwn={() => {
            const title = confirmPlan?.title
            setConfirmPlan(null)
            if (title) navigate({ to: '/goals/new', search: { goal: title } })
          }}
          onUpgrade={() => {
            setConfirmPlan(null)
            setUpgradeOpen(true)
          }}
        />
      </SidebarInset>
    </SidebarProvider>
  )
}
